import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Crown, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Mot de passe réinitialisé");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#f8fafc]" data-testid="reset-page">
      <div className="w-full max-w-sm animate-in-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-xl bg-[#0ea5e9] flex items-center justify-center">
            <Crown className="h-5 w-5 text-white" />
          </div>
          <span className="font-display font-bold text-lg">Vert Distribution</span>
        </div>

        {done ? (
          <div className="text-center py-8" data-testid="reset-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">Mot de passe modifié</h2>
            <p className="text-slate-500 text-sm">Redirection vers la connexion…</p>
          </div>
        ) : !token ? (
          <div className="text-center py-8">
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">Lien invalide</h2>
            <p className="text-slate-500 text-sm mb-6">Ce lien de réinitialisation est incomplet ou a expiré.</p>
            <button data-testid="back-to-login" onClick={() => navigate("/login")} className="text-sky-600 font-semibold text-sm hover:text-sky-700 transition-colors duration-200">Retour à la connexion</button>
          </div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">Sécurité</p>
            <h2 className="font-display text-3xl font-bold text-slate-900 mb-8">Nouveau mot de passe</h2>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nouveau mot de passe</label>
                <div className="relative">
                  <input
                    data-testid="reset-password-input"
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Au moins 6 caractères"
                    className="w-full px-4 py-2.5 pr-11 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors duration-200"
                  />
                  <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200">
                    {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirmer le mot de passe</label>
                <input
                  data-testid="reset-confirm-input"
                  type={show ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors duration-200"
                />
              </div>
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-semibold transition-colors duration-200 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Réinitialiser
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
